import React from "react";
import PropTypes from "prop-types";
import { Col, Flex, Row } from "antd";
import CustomTitle from "../../components/CustomTitle";
import CustomImage from "../../components/CustomImage";
import CustomParagraph from "../../components/CustomParagraph";
import AuthHeader from "../../components/AuthHeader";
import SignupLogo from "../../assets/images/blue-logo.svg";
import LoginLogo from "../../assets/images/b-logo.svg";

/**
 * AuthPage Component
 * 
 * This component renders the authentication page layout with a header, title, description, and an optional image.
 * It uses Ant Design's Row and Col components for responsive layout.
 * 
 * Props:
 * - title: The title text to display.
 * - description: The description text to display.
 * - imageSrc: The source URL for the image.
 * - imageOnLeft: Boolean to determine if the image should be on the left side. Default is true.
 * - children: Any additional components or elements to render within the content section.
 * 
 * Component Structure:
 * - AuthPage
 *   - AuthHeader
 *   - Row
 *     - Col (Image on the left, if imageOnLeft is true)
 *       - CustomImage
 *     - Col (Content section)
 *       - CustomTitle
 *       - CustomParagraph (if description is provided)
 *       - children (additional components/elements)
 *     - Col (Image on the right, if imageOnLeft is false)
 *       - CustomImage
 */

const AuthPage = React.memo(({
  title="",
  description="",
  imageSrc=null,
  imageOnLeft = true,
  pageName,
  children=null,
}) => {
  return (
    <div className="auth-container zl-auth-main">
      {
        pageName === 'Signup' ? 
          <>
            <AuthHeader bgColor="transparent" color="#000" logo={SignupLogo} isSharePage={false} />

            <div className="auth-middle-container">
              <Flex className="signup-container" gap={82} align="center" justify="space-between">
                {imageOnLeft && (
                  <div className="image-container">
                    <img src={imageSrc} alt="" />
                  </div>
                )}

                <div className="auth-content" style={{ maxWidth: "432px" }}>
                  <Flex gap={24} vertical={true}>
                    <CustomTitle
                      text={title}
                      color="#000000"
                      fontSize="44px"
                      fontWeight={900}
                      lineHeight={"52px"}
                      marginBottom={0}
                      className="auth-title"
                    />

                    {description && (
                      <CustomParagraph
                        fontSize="16px"
                        color="#000000"
                        fontWeight={400}
                        lineHeight="24px"
                        text={description}
                      />
                    )}
                  </Flex>
                  {children}
                </div>

                {!imageOnLeft && (
                  <div className="image-container">
                    <img src={imageSrc} alt="" />
                  </div>
                )}
              </Flex>
            </div>
          </>
        :
        null
      }
      
      {
        pageName === 'Login' ? 
          <Flex align="center" className="zl-login-section" style={{ minHeight: "100vh" }}>
            <Flex gap={24} className="zl-login-section" vertical={true} style={{ maxWidth: "432px", margin: "0 auto" }}>
              <div style={{ textAlign: "center" }}>
                <img src={LoginLogo} alt="" />
                <CustomParagraph text="ZoomLogi" fontSize={"27px"} fontWeight={"600"} lineHeight={"49px"} marginBottom={0} />
              </div>

              <main>
                {children}
              </main>
            </Flex>
          </Flex>
        :
        null    
      }
    </div>
  );
});

AuthPage.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  imageSrc: PropTypes.string.isRequired,
  imageOnLeft: PropTypes.bool,
  children: PropTypes.node,
};

// AuthPage.defaultProps = {
//   description: "",
//   imageOnLeft: true,
//   children: null,
// };

export default AuthPage;